/**
 * Migración del alto de fila de la grilla.
 *
 * <b>El problema.</b> `rowHeight` era 148px y el diseño pide 80. No es un ajuste
 * cosmético: <b>148 no tiene granularidad</b>. Una tarjeta de KPI necesita unos
 * 170px —cifra, sparkline y delta— y con 148 sólo se puede pedir 148 (no cabe) o
 * 296 (el doble de lo necesario). Con 80: `2 × 80 + 12 = 172`, exacto.
 *
 * <b>Por qué no un UPDATE masivo.</b> Cambiar `rowHeight` sin tocar los `row_span`
 * guardados encogería todos los widgets existentes casi a la mitad. Y un script que
 * recorra los tenants asume que todos corren la misma versión del kit el mismo día,
 * que en un producto con muchos clientes no es cierto.
 *
 * <b>La salida: perezosa y sellada.</b> La tabla de preferencias ya tiene una
 * columna `config_json` (jsonb). Se estampa ahí `layoutVersion`. La primera vez que
 * un usuario abre el tablero, las filas sin sello se convierten en memoria y el
 * sello viaja en el siguiente guardado. Sin cambios de esquema, sin tocar el
 * contrato del paquete NuGet, idempotente, y cada cliente migra cuando le llega su
 * versión.
 */

/** Alto de fila anterior. */
export const LEGACY_ROW_HEIGHT = 148;

/** Alto de fila actual. */
export const ROW_HEIGHT = 80;

/** Versión del formato de layout. Subirla obliga a una nueva conversión. */
export const LAYOUT_VERSION = 2;

interface MigratablePreference {
  row_span: number;
  config_json?: Record<string, unknown> | null;
}

/**
 * Convierte una preferencia al formato actual si aún no lo está.
 *
 * Devuelve el mismo objeto —por referencia— cuando no hay nada que hacer, para que
 * React no vea un cambio donde no lo hubo.
 */
export function migratePreference<T extends MigratablePreference>(preference: T): T {
  const version = (preference.config_json?.layoutVersion as number | undefined) ?? 1;
  if (version >= LAYOUT_VERSION) return preference;

  /**
   * El alto en píxeles se conserva; lo que cambia es en cuántas filas se expresa.
   * `Math.round` y no `ceil`: redondear siempre hacia arriba haría crecer el
   * tablero entero un poco en cada migración.
   */
  const converted = Math.max(1, Math.round((preference.row_span * LEGACY_ROW_HEIGHT) / ROW_HEIGHT));

  return {
    ...preference,
    row_span: converted,
    config_json: { ...(preference.config_json ?? {}), layoutVersion: LAYOUT_VERSION },
  };
}

/** Aplica la conversión a un layout completo. */
export function migrateLayout<T extends MigratablePreference>(layout: T[]): T[] {
  return layout.map(migratePreference);
}

/** Si un layout tiene alguna fila pendiente de convertir. */
export function needsMigration<T extends MigratablePreference>(layout: T[]): boolean {
  return layout.some(
    pref => ((pref.config_json?.layoutVersion as number | undefined) ?? 1) < LAYOUT_VERSION,
  );
}
